<template>
    <div class="action-dropdown" v-if="orderId">
        <md-menu md-size="medium" md-align-trigger>
            <md-button md-menu-trigger class="md-button" title="Actions">
                <md-icon>more_horiz</md-icon>
            </md-button>

            <md-menu-content>
                <md-menu-item @click="updateOrder()">
                    <md-icon>edit</md-icon>
                    <span>
                        Update Order
                    </span>
                </md-menu-item>

                <md-menu-item @click="putOrderOnHold()">
                    <md-icon>{{ status == 'On Hold' ? 'play_circle_outline' : 'pause' }}</md-icon>
                    <span class="menu-item">
                        Place on Hold
                    </span>
                </md-menu-item>

                <md-menu-item @click="cancelOrder()" class="md-danger">
                    <md-icon>close</md-icon>
                    <span class="menu-item">
                        Cancel Order
                    </span>
                </md-menu-item>

                <md-divider></md-divider>

                <md-menu-item @click="handleDelete()" :class="[{ 'btn-disabled': !canDelete }]" :disabled="!canDelete">
                    <md-icon>delete</md-icon>
                    <span class="menu-item">
                        Delete Order
                    </span>
                    <md-tooltip v-if="!canDelete">You cannot delete a delivered/cancelled order.</md-tooltip>
                </md-menu-item>
            </md-menu-content>
        </md-menu>
    </div>
</template>

<script>
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins';

export default {
    name: 'ActionRenderer',
    mixins: [GeneralMixin],
    data() {
        return {
            orderId: null,
            status: null,
            canDelete: false
        };
    },
    beforeMount() {
        this.updateData(this.params);
    },
    methods: {
        updateData(params) {
            this.orderId = params.data.orderId;
            this.status = params.data.status;
            this.canDelete = params.data.canDelete;
        },
        updateOrder() {
            const route = this.$router.resolve({ name: 'Edit Order', params: { orderId: this.orderId } });
            window.open(route.href, '_blank');
        },
        putOrderOnHold() {
            let title = 'Order on Hold';
            let bodyMessage =
                'Are you sure you want to put this order on hold? This will also put the related stops to an on hold status.';
            if (this.status === 'On Hold') {
                title = 'Order Unhold';
                bodyMessage = 'Are you sure you want to unhold this order? This will also unhold the related stops.';
            }

            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title,
                    body: bodyMessage,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        let apiAction = 'on-hold';
                        if (this.status === 'On Hold') {
                            apiAction = 'unhold';
                        }
                        await this.updateOrderStatus(this.orderId, apiAction);
                    }
                });
        },
        cancelOrder() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Cancel Order',
                    body: `Are you sure you want to cancel this order. This can't be undone?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        await this.updateOrderStatus(this.orderId, 'cancel');
                    }
                });
        },
        async updateOrderStatus(orderId, action) {
            this.$_handleLoaderState(true, 'UPDATING...');
            const api = `/api/orders/${orderId}/${action}`;
            const payload = {
                method: 'post'
            };

            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Successfully updated the status.',
                    type: 'success'
                });
                this.params.context.componentParent.refreshServerSide();
            } catch (e) {
                const message = 'Could not update order status.';
                showErrorMessage(this, message, e);
            }
            this.$_handleLoaderState(false);
        },
        handleDelete() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Delete Order',
                    body:
                        'Deleting this order will also delete associated stop and items. Are you sure you want to proceed?',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') 
                        await this.handleConfirmDelete();
                });
        },
        async handleConfirmDelete() {
            this.$_handleLoaderState(true, 'DELETING...');
            const payload = {
                method: 'delete'
            };
            const api = `/api/orders/${this.orderId}`;
            try {
                await handleRequests(api, payload);

                this.$notify({
                    message: 'Order was deleted successfully.',
                    type: 'success'
                });
                this.params.context.componentParent.refreshServerSide();
                this.$_handleLoaderState(false);
            } catch (e) {
                if ((e.status === 400 || e.status === 401) && e.data?.length > 0) {
                    let messageObj = {
                        type: 'danger',
                        message: e.data[0]
                    };

                    if (e.data?.length > 1) {
                        const message = e.data.map((error) => `• ${error}`).join('\n');
                        messageObj = {
                            ...messageObj,
                            dangerouslyUseHTMLString: true,
                            message: `<div style="white-space: pre-line">${message}</div>`
                        };
                    }

                    this.$notify(messageObj);
                } else {
                    this.$notify({
                        type: 'danger',
                        message: 'Unable to delete order.'
                    });
                }
                this.$_handleLoaderState(false);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.action-dropdown {
    ::v-deep .md-button {
        min-width: 50px;
        background-color: white !important;
        border: 1px solid #000;
        height: 30px;
        vertical-align: middle;

        .md-button-content i {
            color: black !important;
        }
    }

    ::v-deep .md-ripple {
        padding: 10px !important;
    }
}

::v-deep .md-divider {
    margin: 0px 0 4px 0;
}

::v-deep .md-list-item-button {
    .md-icon {
        margin-right: 5px !important;
    }

    &:hover {
        .md-icon {
            color: #fff !important;
        }
    }

    span {
        padding: 0 !important;
        left: 50px !important;
        top: 14px;
    }

    .md-ripple {
        padding: 5px 12px !important;
    }
}

.btn-disabled {
    span,
    i {
        color: #aaa !important;
    }
}
</style>
